import React, { useRef, useState } from 'react';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { bedtimeAPI, studentAPI } from '@/lib/api';
import { BrutalCard, BrutalButton } from '@/components/brutal';
import { Moon, Play, Download, Video, Upload, X, Pencil, Globe, Check, Mic, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import CloneVoiceModal from '@/components/guardian/CloneVoiceModal';

const TYPE_BY_EXT = { mp4: 'video/mp4', mov: 'video/quicktime', m4v: 'video/x-m4v', webm: 'video/webm' };
const CONSENT_TEXT =
  'I am this child\'s parent or guardian and I appear in this video. I agree that Semantic Vision stores it permanently for my family. I can download it any time.';

const contentTypeOf = (file) => {
  if (file.type && file.type.startsWith('video/')) return file.type;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return TYPE_BY_EXT[ext] || '';
};

const SHARE_TEXT =
  'I am happy for every child on Semantic Vision to watch this video, and I have the right to share it. Semantic Vision may take it off the shared shelf at any time, and I can stop sharing whenever I like.';

const VISIBILITY_LABEL = {
  pending_public: ['Waiting for review', 'bg-amber-100 text-amber-800'],
  public: ['Shared with everyone', 'bg-emerald-100 text-emerald-800'],
};

const AI_VOICE_TEXT =
  'I am happy for an AI copy of my voice to read this story to my children. It is my own voice, and the audio stays in my family unless I choose to share it.';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const BedtimeStoriesTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const chooseRef = useRef(null);
  const recordRef = useRef(null);

  const [title, setTitle] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [file, setFile] = useState(null);
  const [selectedKids, setSelectedKids] = useState([]);
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState(null); // null = idle, 0-100 = uploading
  const [watching, setWatching] = useState(null); // { story, url }
  const [editing, setEditing] = useState(null); // { id, title, story_title, student_ids }
  const [shareOk, setShareOk] = useState(false);
  const isAdmin = user?.role === 'admin';

  // "Read a story in my voice"
  const [cloneOpen, setCloneOpen] = useState(false);
  const [voiceId, setVoiceId] = useState('');
  const [aiNarrativeId, setAiNarrativeId] = useState('');
  const [aiChapters, setAiChapters] = useState([]); // [] = whole story
  const [aiKids, setAiKids] = useState([]);
  const [aiTitle, setAiTitle] = useState('');
  const [aiConsent, setAiConsent] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const { data: allStudents = [] } = useQuery({
    queryKey: ['bedtime-students', user?.id], // own key: ['students', ...] is shared with the Students tab, which loads every child for an admin
    queryFn: async () => (await studentAPI.getAll(user?.id)).data,
    enabled: !!user?.id,
  });
  // An admin login is sent every student in the system. Stories can only be made for
  // your own children, so only offer those.
  const students = allStudents.filter((s) => !s.guardian_id || s.guardian_id === user?.id);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['bedtime-stories'],
    initialPageParam: null,
    queryFn: async ({ pageParam }) =>
      (await bedtimeAPI.list({ limit: 20, cursor: pageParam || undefined })).data,
    getNextPageParam: (last) => last.next_cursor || undefined,
  });
  const stories = (data?.pages || []).flatMap((p) => p.stories || []);

  const kidName = (id) => students.find((s) => s.id === id)?.full_name;
  const toggleKid = (id) =>
    setSelectedKids((cur) => (cur.includes(id) ? cur.filter((k) => k !== id) : [...cur, id]));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!contentTypeOf(f)) {
      toast.error('Please choose an MP4, MOV, M4V or WebM video.');
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').slice(0, 120));
  };

  const canSend = !!file && title.trim() && consent && progress === null;

  const upload = async () => {
    const contentType = contentTypeOf(file);
    setProgress(0);
    try {
      const { data: start } = await bedtimeAPI.start({
        title: title.trim(),
        story_title: storyTitle.trim(),
        content_type: contentType,
        student_ids: selectedKids,
        consent,
      });
      await bedtimeAPI.putToStorage(start.upload_url, file, contentType, setProgress);
      await bedtimeAPI.complete(start.id);
      toast.success('Bedtime story saved!');
      setFile(null); setTitle(''); setStoryTitle(''); setSelectedKids([]); setConsent(false);
      queryClient.invalidateQueries({ queryKey: ['bedtime-stories'] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'We could not send the video. Please check your connection and try again.');
    } finally {
      setProgress(null);
    }
  };

  const { data: voices = [], refetch: refetchVoices } = useQuery({
    queryKey: ['bedtime-voices'],
    queryFn: async () => (await bedtimeAPI.voices()).data,
  });
  const { data: aiOptions, refetch: refetchAiOptions } = useQuery({
    queryKey: ['bedtime-ai-options'],
    queryFn: async () => (await bedtimeAPI.aiOptions()).data,
  });
  React.useEffect(() => { if (!voiceId && voices.length) setVoiceId(voices[0].id); }, [voices, voiceId]);
  const aiStory = aiOptions?.stories?.find((x) => x.id === aiNarrativeId);
  const aiChars = aiStory
    ? aiStory.chapters.filter((c) => !aiChapters.length || aiChapters.includes(c.number)).reduce((n, c) => n + c.chars, 0)
    : 0;
  const aiLeft = aiOptions?.limit_chars == null ? null : Math.max(0, aiOptions.limit_chars - aiOptions.used_chars);
  const aiTooLong = aiChars > (aiOptions?.max_chars_per_story || 12000);
  const aiOverAllowance = aiLeft != null && aiChars > aiLeft;
  const canMakeAi = !!(voiceId && aiNarrativeId && aiChars > 0 && aiConsent && !aiTooLong && !aiOverAllowance && !aiBusy);

  const toggleAiChapter = (n) =>
    setAiChapters((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  const toggleAiKid = (id) =>
    setAiKids((cur) => (cur.includes(id) ? cur.filter((k) => k !== id) : [...cur, id]));

  const makeAiStory = async () => {
    setAiBusy(true);
    try {
      await bedtimeAPI.aiCreate({
        narrative_id: aiNarrativeId, voice_id: voiceId, chapter_numbers: aiChapters,
        title: aiTitle.trim() || undefined, student_ids: aiKids, consent: aiConsent,
      });
      toast.success('Your bedtime story is ready!');
      setAiNarrativeId(''); setAiChapters([]); setAiKids([]); setAiTitle(''); setAiConsent(false);
      refetchAiOptions();
      queryClient.invalidateQueries({ queryKey: ['bedtime-stories'] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'We could not make the story. Please try again.');
    } finally {
      setAiBusy(false);
    }
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['bedtime-stories'] });
    queryClient.invalidateQueries({ queryKey: ['bedtime-pending'] });
  };

  const startEdit = (story) => {
    setShareOk(false);
    setEditing({ id: story.id, title: story.title, story_title: story.story_title || '', student_ids: story.student_ids || [] });
  };
  const toggleEditKid = (id) =>
    setEditing((e) => ({ ...e, student_ids: e.student_ids.includes(id) ? e.student_ids.filter((k) => k !== id) : [...e.student_ids, id] }));

  const saveEdit = async () => {
    try {
      await bedtimeAPI.update(editing.id, { title: editing.title, story_title: editing.story_title, student_ids: editing.student_ids });
      toast.success('Saved');
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the changes.');
    }
  };

  const shareStory = async (story) => {
    try {
      const { data: res } = await bedtimeAPI.share(story.id, shareOk);
      toast.success(res.visibility === 'public' ? 'Now shared with every child.' : 'Sent for review. We will share it once approved.');
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not share this story.');
    }
  };

  const stopSharing = async (story) => {
    try {
      await bedtimeAPI.unshare(story.id);
      toast.success('Back to your family only.');
      refresh();
    } catch {
      toast.error('Could not change sharing.');
    }
  };

  const { data: pending } = useQuery({
    queryKey: ['bedtime-pending'],
    queryFn: async () => (await bedtimeAPI.pending()).data,
    enabled: isAdmin,
  });

  const reviewStory = async (story, approve) => {
    try {
      await bedtimeAPI.review(story.id, approve);
      toast.success(approve ? 'Approved. Every child can now watch it.' : 'Declined. It stays private.');
      refresh();
    } catch {
      toast.error('Could not save your decision.');
    }
  };

  const watchPending = async (story) => {
    try {
      const { data: res } = await bedtimeAPI.adminPlay(story.id);
      setWatching({ story, url: res.url });
    } catch {
      toast.error('This video is not available right now.');
    }
  };

  const watch = async (story) => {
    try {
      const { data: res } = await bedtimeAPI.play(story.id);
      setWatching({ story, url: res.url });
    } catch {
      toast.error('This video is not available right now.');
    }
  };

  const save = async (story) => {
    try {
      const { data: res } = await bedtimeAPI.download(story.id);
      window.location.assign(res.url);
    } catch {
      toast.error('Could not start the download. Please try again.');
    }
  };

  return (
    <div className="space-y-6" data-testid="bedtime-stories-tab">
      <BrutalCard shadow="xl" className="bg-gradient-to-r from-indigo-50 to-violet-50">
        <div className="flex items-center gap-3">
          <Moon size={28} className="text-indigo-600" />
          <div>
            <h2 className="text-2xl font-black uppercase">Bedtime Stories</h2>
            <p className="text-gray-600 font-medium text-sm">
              Record yourself reading to your child. They can watch it any night, even when you are away.
            </p>
          </div>
        </div>
      </BrutalCard>

      <BrutalCard shadow="lg">
        <h3 className="text-lg font-black uppercase mb-3">Add a bedtime story</h3>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <BrutalButton variant="indigo" size="sm" onClick={() => recordRef.current?.click()} disabled={progress !== null}>
              <Video size={14} className="inline mr-1" /> Record now
            </BrutalButton>
            <BrutalButton variant="default" size="sm" onClick={() => chooseRef.current?.click()} disabled={progress !== null}>
              <Upload size={14} className="inline mr-1" /> Choose a video
            </BrutalButton>
            <input ref={recordRef} id="bedtime-record" type="file" accept="video/*" capture="user" className="hidden" onChange={pickFile} />
            <input ref={chooseRef} id="bedtime-choose" type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/*" className="hidden" onChange={pickFile} />
          </div>

          {file && (
            <p className="text-sm font-medium text-gray-700" data-testid="bedtime-file">
              Ready to send: <b>{file.name}</b> ({(file.size / 1048576).toFixed(1)} MB)
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-bold" htmlFor="bedtime-title">
              Name this story
              <input id="bedtime-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                className="mt-1 w-full border-2 border-black/15 rounded-lg px-3 py-2 font-medium bg-white" placeholder="Goodnight, little one" />
            </label>
            <label className="text-sm font-bold" htmlFor="bedtime-book">
              Book you read (optional)
              <input id="bedtime-book" value={storyTitle} onChange={(e) => setStoryTitle(e.target.value)} maxLength={160}
                className="mt-1 w-full border-2 border-black/15 rounded-lg px-3 py-2 font-medium bg-white" placeholder="Goodnight Moon" />
            </label>
          </div>

          {students.length > 0 && (
            <div>
              <p className="text-sm font-bold mb-1">Who is it for? <span className="font-medium text-gray-500">(leave empty for the whole family)</span></p>
              <div className="flex flex-wrap gap-2">
                {students.map((s) => (
                  <button key={s.id} type="button" onClick={() => toggleKid(s.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${selectedKids.includes(s.id) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                    {s.full_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm font-medium" htmlFor="bedtime-consent">
            <input id="bedtime-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
            <span>{CONSENT_TEXT}</span>
          </label>

          {progress !== null && (
            <div data-testid="bedtime-progress" aria-live="polite">
              <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs font-bold text-gray-600 mt-1">Sending your video… {progress}%. Please keep this page open.</p>
            </div>
          )}

          <BrutalButton variant="emerald" onClick={upload} disabled={!canSend} data-testid="bedtime-send">
            {progress !== null ? 'Sending…' : 'Save bedtime story'}
          </BrutalButton>
        </div>
      </BrutalCard>

      <BrutalCard shadow="lg" data-testid="bedtime-ai-voice">
        <h3 className="text-lg font-black uppercase mb-1 flex items-center gap-2"><Mic size={18} /> Read a story in your voice</h3>
        <p className="text-sm text-gray-600 mb-3">Pick one of your children's stories and an AI copy of your voice reads it aloud. It takes about 30 seconds and is saved for bedtime.</p>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-bold mb-1">1. Your voice</p>
            {voices.length === 0 ? (
              <p className="text-sm text-gray-500 mb-2">You have not added your voice yet. Record about a minute of yourself reading.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mb-2">
                {voices.map((v) => (
                  <button key={v.id} type="button" onClick={() => setVoiceId(v.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${voiceId === v.id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                    {v.name}
                  </button>
                ))}
              </div>
            )}
            <BrutalButton variant="default" size="sm" onClick={() => setCloneOpen(true)}><Mic size={14} className="inline mr-1" /> Add my voice</BrutalButton>
          </div>

          <div>
            <p className="text-sm font-bold mb-1">2. The story</p>
            {!aiOptions?.stories?.length ? (
              <p className="text-sm text-gray-500">No stories yet. Make a story for one of your children first, then come back.</p>
            ) : (
              <select id="bedtime-ai-story" value={aiNarrativeId} onChange={(e) => { setAiNarrativeId(e.target.value); setAiChapters([]); }}
                className="w-full border-2 border-black/15 rounded-lg px-3 py-2 font-medium bg-white">
                <option value="">Choose a story…</option>
                {aiOptions.stories.map((x) => (
                  <option key={x.id} value={x.id}>{x.title}{x.student_name ? ` (${x.student_name})` : ''}</option>
                ))}
              </select>
            )}
            {aiStory && aiStory.chapters.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {aiStory.chapters.map((c) => (
                  <button key={c.number} type="button" onClick={() => toggleAiChapter(c.number)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${aiChapters.includes(c.number) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                    Chapter {c.number}
                  </button>
                ))}
                <span className="text-xs text-gray-500 self-center">(none picked = the whole story)</span>
              </div>
            )}
            {aiStory && (
              <p className={`text-xs font-bold mt-2 ${aiTooLong || aiOverAllowance ? 'text-rose-600' : 'text-gray-600'}`}>
                {aiChars.toLocaleString()} characters
                {aiTooLong ? ` is too long. Pick fewer chapters (limit ${(aiOptions.max_chars_per_story).toLocaleString()}).` : ''}
                {!aiTooLong && aiOverAllowance ? ` is more than the ${aiLeft.toLocaleString()} you have left this month.` : ''}
                {!aiTooLong && !aiOverAllowance && aiLeft != null ? `. You have ${aiLeft.toLocaleString()} left this month.` : ''}
              </p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-bold" htmlFor="bedtime-ai-title">3. Name it (optional)
              <input id="bedtime-ai-title" value={aiTitle} onChange={(e) => setAiTitle(e.target.value)} maxLength={120}
                className="mt-1 w-full border-2 border-black/15 rounded-lg px-3 py-2 font-medium bg-white" placeholder="Mom reads the moon story" />
            </label>
            {students.length > 0 && (
              <div>
                <p className="text-sm font-bold mb-1">Who is it for? <span className="font-medium text-gray-500">(none = whole family)</span></p>
                <div className="flex flex-wrap gap-2">
                  {students.map((st) => (
                    <button key={st.id} type="button" onClick={() => toggleAiKid(st.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${aiKids.includes(st.id) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                      {st.full_name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm font-medium" htmlFor="bedtime-ai-consent">
            <input id="bedtime-ai-consent" type="checkbox" checked={aiConsent} onChange={(e) => setAiConsent(e.target.checked)} className="mt-1" />
            <span>{AI_VOICE_TEXT}</span>
          </label>

          <BrutalButton variant="emerald" onClick={makeAiStory} disabled={!canMakeAi} data-testid="bedtime-ai-make">
            {aiBusy ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Reading your story… about 30 seconds</span> : 'Make my bedtime story'}
          </BrutalButton>
        </div>
      </BrutalCard>

      <CloneVoiceModal open={cloneOpen} onClose={() => setCloneOpen(false)}
        onCreated={() => { setCloneOpen(false); refetchVoices(); }} />

      {isAdmin && pending?.stories?.length > 0 && (
        <BrutalCard shadow="lg" className="border-amber-300" data-testid="bedtime-review">
          <h3 className="text-lg font-black uppercase mb-1">Waiting for your review</h3>
          <p className="text-sm text-gray-600 mb-3">Watch each story first. Approving it shows it to every child on Semantic Vision.</p>
          <div className="space-y-2">
            {pending.stories.map((story) => (
              <div key={story.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 p-3">
                <div className="min-w-0">
                  <p className="font-bold truncate">{story.title}</p>
                  <p className="text-xs text-gray-500">{story.story_title ? `${story.story_title} · ` : ''}{formatDate(story.created_date)}</p>
                </div>
                <div className="flex gap-2">
                  <BrutalButton size="sm" variant="default" onClick={() => watchPending(story)}><Play size={12} className="inline mr-1" /> Watch</BrutalButton>
                  <BrutalButton size="sm" variant="emerald" onClick={() => reviewStory(story, true)}>Approve</BrutalButton>
                  <BrutalButton size="sm" variant="rose" onClick={() => reviewStory(story, false)}>Decline</BrutalButton>
                </div>
              </div>
            ))}
          </div>
        </BrutalCard>
      )}

      {isLoading ? (
        <p className="text-center font-bold text-gray-500 py-8">Loading your stories…</p>
      ) : stories.length === 0 ? (
        <BrutalCard shadow="lg" className="text-center py-10">
          <Moon size={44} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-xl font-black mb-1">No bedtime stories yet</h3>
          <p className="text-gray-500 font-medium text-sm">Record your first one above. It will wait here for every bedtime.</p>
        </BrutalCard>
      ) : (
        <div className="space-y-3">
          {stories.map((story) => (
            <BrutalCard key={story.id} shadow="md" data-testid={`bedtime-${story.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={() => watch(story)} aria-label={`Play ${story.title}`}
                    className="w-11 h-11 shrink-0 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                    <Play size={18} />
                  </button>
                  <div className="min-w-0">
                    <p className="font-bold truncate">
                      {story.title}
                      {VISIBILITY_LABEL[story.visibility] && (
                        <span className={`ml-2 align-middle text-[10px] font-bold px-2 py-0.5 rounded-full ${VISIBILITY_LABEL[story.visibility][1]}`}>
                          {VISIBILITY_LABEL[story.visibility][0]}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {story.story_title ? `${story.story_title} · ` : ''}
                      {story.student_ids?.length ? `For ${story.student_ids.map(kidName).filter(Boolean).join(', ') || 'your children'}` : 'For the whole family'}
                      {' · '}{formatDate(story.created_date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center shrink-0">
                  <button onClick={() => (editing?.id === story.id ? setEditing(null) : startEdit(story))} title="Change who it is for or share it" aria-label={`Edit ${story.title}`}
                    className="p-2 text-gray-400 hover:text-indigo-600 transition-all" data-testid={`bedtime-edit-${story.id}`}>
                    <Pencil size={18} />
                  </button>
                  <button onClick={() => save(story)} title="Download this story" aria-label={`Download ${story.title}`}
                    className="p-2 text-gray-400 hover:text-indigo-600 transition-all">
                    <Download size={18} />
                  </button>
                </div>
              </div>
              {editing?.id === story.id && (
                <div className="mt-4 pt-4 border-t space-y-4" data-testid="bedtime-editor">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-sm font-bold" htmlFor={`edit-title-${story.id}`}>Name
                      <input id={`edit-title-${story.id}`} value={editing.title} maxLength={120} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                        className="mt-1 w-full border-2 border-black/15 rounded-lg px-3 py-2 font-medium bg-white" />
                    </label>
                    <label className="text-sm font-bold" htmlFor={`edit-book-${story.id}`}>Book
                      <input id={`edit-book-${story.id}`} value={editing.story_title} maxLength={160} onChange={(e) => setEditing({ ...editing, story_title: e.target.value })}
                        className="mt-1 w-full border-2 border-black/15 rounded-lg px-3 py-2 font-medium bg-white" />
                    </label>
                  </div>
                  <div>
                    <p className="text-sm font-bold mb-1">Who is it for? <span className="font-medium text-gray-500">(none picked = the whole family)</span></p>
                    <div className="flex flex-wrap gap-2">
                      {students.map((s) => (
                        <button key={s.id} type="button" onClick={() => toggleEditKid(s.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold ${editing.student_ids.includes(s.id) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                          {s.full_name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <BrutalButton variant="emerald" size="sm" onClick={saveEdit}><Check size={14} className="inline mr-1" /> Save changes</BrutalButton>
                    <BrutalButton variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</BrutalButton>
                  </div>

                  <div className="rounded-lg bg-indigo-50 p-3 space-y-2">
                    <p className="text-sm font-black flex items-center gap-1"><Globe size={14} /> Share with every child on Semantic Vision</p>
                    {story.visibility === 'public' || story.visibility === 'pending_public' ? (
                      <>
                        <p className="text-sm text-gray-700">
                          {story.visibility === 'public' ? 'Every child can watch this story now.' : 'We are reviewing this story. It will appear for every child once approved.'}
                        </p>
                        <BrutalButton variant="default" size="sm" onClick={() => stopSharing(story)}>Stop sharing</BrutalButton>
                      </>
                    ) : (
                      <>
                        <label className="flex items-start gap-2 text-sm font-medium" htmlFor={`share-ok-${story.id}`}>
                          <input id={`share-ok-${story.id}`} type="checkbox" checked={shareOk} onChange={(e) => setShareOk(e.target.checked)} className="mt-1" />
                          <span>{SHARE_TEXT}</span>
                        </label>
                        <BrutalButton variant="indigo" size="sm" disabled={!shareOk} onClick={() => shareStory(story)}>
                          {isAdmin ? 'Share with everyone now' : 'Ask to share with everyone'}
                        </BrutalButton>
                      </>
                    )}
                  </div>
                </div>
              )}
            </BrutalCard>
          ))}
          {hasNextPage && (
            <div className="text-center pt-2">
              <BrutalButton onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading…' : 'Load more stories'}
              </BrutalButton>
            </div>
          )}
        </div>
      )}

      {watching && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setWatching(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <p className="font-bold text-sm">{watching.story.title}</p>
              <button onClick={() => setWatching(null)} aria-label="Close" className="text-gray-400 hover:text-gray-800"><X size={18} /></button>
            </div>
            {watching.story.content_type?.startsWith('audio/') ? (
              <div className="p-6 flex flex-col items-center gap-4 bg-indigo-50">
                <Moon size={56} className="text-indigo-600" />
                <audio src={watching.url} controls autoPlay className="w-full"
                  onError={() => { setWatching(null); toast.error('Story not available'); }} />
              </div>
            ) : (
              <video src={watching.url} controls autoPlay playsInline className="w-full max-h-[75vh] bg-black"
                onError={() => { setWatching(null); toast.error('Video not available'); }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BedtimeStoriesTab;
